import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../../services/supabase';
import { getTodayISO, formatDateForDisplay } from '../../utils/dateUtils';
import GanttChart from './GanttChart';
import EditReservationModal from './EditReservationModal';
import DatePicker from '../Common/DatePicker';

function ReservationManagement() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(getTodayISO());
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingReservation, setEditingReservation] = useState(null);
  const [maxGuestsPerSlot, setMaxGuestsPerSlot] = useState(6);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadReservations();
    loadMaxGuestsSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate, filterStatus, searchQuery]);

  const loadMaxGuestsSettings = async () => {
    try {
      const { data, error } = await supabaseClient
        .from('store_settings')
        .select('setting_value')
        .eq('setting_key', 'max_guests_per_slot')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('設定読み込みエラー:', error);
        return;
      }

      if (data && data.setting_value) {
        setMaxGuestsPerSlot(data.setting_value.max || 6);
      }
    } catch (error) {
      console.error('設定読み込みエラー:', error);
    }
  };

  const saveMaxGuestsSettings = async () => {
    setSavingSettings(true);
    try {
      const { error } = await supabaseClient
        .from('store_settings')
        .upsert(
          {
            setting_key: 'max_guests_per_slot',
            setting_value: { max: parseInt(maxGuestsPerSlot) },
          },
          { onConflict: 'setting_key' }
        );

      if (error) throw error;
      alert('予約人数制限を保存しました。');
    } catch (error) {
      console.error('設定保存エラー:', error);
      alert('設定の保存に失敗しました。');
    } finally {
      setSavingSettings(false);
    }
  };

  const loadReservations = async () => {
    setLoading(true);
    try {
      let query = supabaseClient
        .from('reservations')
        .select('*')
        .order('reservation_date', { ascending: true })
        .order('reservation_time', { ascending: true });

      if (filterDate) {
        query = query.eq('reservation_date', filterDate);
      }
      if (filterStatus) {
        query = query.eq('status', filterStatus);
      }
      if (searchQuery) {
        query = query.or(
          `customer_name.ilike.%${searchQuery}%,customer_phone.ilike.%${searchQuery}%,customer_email.ilike.%${searchQuery}%`
        );
      }

      const { data, error } = await query;

      if (error) throw error;
      setReservations(data || []);
    } catch (error) {
      console.error('予約読み込みエラー:', error);
      alert('予約データの読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const statusLabels = {
    pending: '店舗確認中',
    confirmed: '確定',
    cancelled: 'キャンセル',
    visited: '来店済み',
    no_show: '無断キャンセル',
  };

  return (
    <section className="admin-section">
      <div className="container">
        <h2>予約管理(時間管理)</h2>

        <div className="filter-section">
          <div className="filter-group">
            <label>日付検索</label>
            <DatePicker
              value={filterDate}
              onChange={(date) => setFilterDate(date)}
              placeholder="日付を選択"
            />
          </div>
          <div className="filter-group">
            <label>ステータス</label>
            <select
              id="filterStatus"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">すべて</option>
              <option value="pending">店舗確認中</option>
              <option value="confirmed">確定</option>
              <option value="cancelled">キャンセル</option>
              <option value="visited">来店済み</option>
              <option value="no_show">無断キャンセル</option>
            </select>
          </div>
          <div className="filter-group">
            <label>検索</label>
            <input
              type="text"
              id="searchQuery"
              placeholder="名前、電話番号、メールアドレス"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="btn-primary" onClick={loadReservations}>
            検索
          </button>
        </div>

        {loading ? (
          <div>読み込み中...</div>
        ) : (
          <>
            <div className="reservations-table-container">
              <table className="reservations-table">
                <thead>
                  <tr>
                    <th>予約日時</th>
                    <th>お名前</th>
                    <th>電話番号</th>
                    <th>メールアドレス</th>
                    <th>人数</th>
                    <th>席種</th>
                    <th>ステータス</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.length === 0 ? (
                    <tr>
                      <td colSpan="9">予約がありません</td>
                    </tr>
                  ) : (
                    reservations.map((reservation) => (
                      <tr key={reservation.id}>
                        <td>
                          {formatDateForDisplay(reservation.reservation_date)}{' '}
                          {reservation.reservation_time}
                        </td>
                        <td>{reservation.customer_name || '-'}</td>
                        <td>{reservation.customer_phone || '-'}</td>
                        <td>{reservation.customer_email || '-'}</td>
                        <td>
                          大人{reservation.adult_count}名、子供
                          {reservation.child_count}名
                        </td>
                        <td>
                          {reservation.seat_type === 'counter'
                            ? 'カウンター'
                            : 'テーブル'}
                        </td>
                        <td>
                          <span
                            className={`status-badge status-${reservation.status}`}
                          >
                            {statusLabels[reservation.status] || reservation.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn-edit"
                            onClick={() => {
                              setEditingReservation(reservation);
                            }}
                          >
                            編集
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="gantt-section">
              <h3>予約状況可視化（ガントチャート）</h3>
              <GanttChart date={filterDate || getTodayISO()} />
            </div>

            {/* 予約人数制限設定 */}
            <div className="settings-form" style={{ marginTop: '30px' }}>
              <h3>予約人数制限設定</h3>
              <p style={{ color: '#cccccc', marginBottom: '15px' }}>
                予約開始時間から30分間の範囲で、予約できる人数の上限を設定します。この人数を超える予約がある時間帯は、お客様が選択できなくなります。
              </p>
              <div className="form-group" style={{ maxWidth: '300px' }}>
                <label>同時刻の予約人数上限（30分間）</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={maxGuestsPerSlot}
                    onChange={(e) => setMaxGuestsPerSlot(e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <span style={{ color: '#ffffff' }}>名まで</span>
                  <button
                    className="btn-primary"
                    onClick={saveMaxGuestsSettings}
                    disabled={savingSettings}
                    style={{ marginLeft: '10px' }}
                  >
                    {savingSettings ? '保存中...' : '保存'}
                  </button>
                </div>
                <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '8px' }}>
                  ※例: 6名に設定した場合、予約開始時間から30分以内に既に6名以上の予約があると、その時間は選択不可になります。
                </p>
              </div>
            </div>
          </>
        )}

        {editingReservation && (
          <EditReservationModal
            reservation={editingReservation}
            onClose={() => setEditingReservation(null)}
            onUpdate={() => {
              loadReservations();
            }}
          />
        )}
      </div>
    </section>
  );
}

export default ReservationManagement;

